'use client';

import {
  useCallback,
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { Button } from '../../../components/ui/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { useShareMatchResultsEmailMutation } from '../hooks/useShareMatchResultsEmailMutation';

const EMAIL_MAX = 320;
const NAME_MAX = 80;

function isValidEmail(value: string): boolean {
  const v = value.trim();
  if (v.length < 3 || v.length > EMAIL_MAX) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export type MatchShareEmailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchResultsId: string | null;
  /**
   * Same `--theme-*` map as the adopt-quiz / share overlay so the native `<dialog>`
   * top layer matches the selected shelter palette (not `:root` public).
   */
  shelterThemeVars?: Record<string, string> | null;
};

export function MatchShareEmailDialog({
  open,
  onOpenChange,
  matchResultsId,
  shelterThemeVars = null,
}: MatchShareEmailDialogProps) {
  const emailMutation = useShareMatchResultsEmailMutation();
  const formId = useId();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [shareConsentChecked, setShareConsentChecked] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) return;
    setEmail('');
    setDisplayName('');
    setShareConsentChecked(false);
    setLocalError(null);
  }, [open]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      if (!matchResultsId) {
        setLocalError('Share link is not available. Run Find matches again.');
        return;
      }
      if (!isValidEmail(email)) {
        setLocalError('Enter a valid email address.');
        return;
      }
      if (!shareConsentChecked) {
        setLocalError('You must agree to share your data before sending email.');
        return;
      }
      const name =
        displayName.trim().length > 0
          ? displayName.trim().slice(0, NAME_MAX)
          : undefined;
      emailMutation.mutate(
        {
          matchResultsId,
          recipientEmail: email.trim(),
          recipientName: name,
        },
        {
          onSuccess: (data) => {
            if (data.sent) {
              onOpenChange(false);
            } else {
              setLocalError(
                'Email sending is disabled in this environment. Try again later.',
              );
            }
          },
          onError: () => {
            setLocalError('Could not send email. Please try again.');
          },
        },
      );
    },
    [
      email,
      displayName,
      matchResultsId,
      shareConsentChecked,
      emailMutation,
      onOpenChange,
    ],
  );

  const busy = emailMutation.isPending;

  const surfaceThemeStyle = shelterThemeVars
    ? (shelterThemeVars as CSSProperties)
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      aria-label='Email results'
      className='z-100'
      appTheme={shelterThemeVars ? 'adopt-quiz' : undefined}
      surfaceThemeStyle={surfaceThemeStyle}
    >
      <DialogContent showCloseButton>
        <DialogHeader>
          <DialogTitle>Email your match results</DialogTitle>
          <p className='text-sm text-muted-foreground'>
            We&apos;ll send a summary with your ranked matches to the address
            below.
          </p>
        </DialogHeader>

        <form
          id={formId}
          className='flex min-h-0 flex-1 flex-col'
          onSubmit={handleSubmit}
        >
          <DialogBody className='space-y-4'>
          <div>
            <label
              htmlFor={`${formId}-email`}
              className='block text-sm font-medium text-foreground'
            >
              Email
            </label>
            <input
              id={`${formId}-email`}
              type='email'
              name='email'
              autoComplete='email'
              required
              maxLength={EMAIL_MAX}
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              disabled={busy || !matchResultsId}
              className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-primary-600 focus-visible:ring-2 disabled:opacity-60'
              placeholder='you@example.com'
            />
          </div>
          <div>
            <label
              htmlFor={`${formId}-name`}
              className='block text-sm font-medium text-foreground'
            >
              First name <span className='font-normal'>(optional)</span>
            </label>
            <input
              id={`${formId}-name`}
              type='text'
              name='name'
              autoComplete='given-name'
              maxLength={NAME_MAX}
              value={displayName}
              onChange={(ev) => setDisplayName(ev.target.value)}
              disabled={busy}
              className='mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none ring-primary-600 focus-visible:ring-2 disabled:opacity-60'
              placeholder='Ashley'
            />
          </div>
          <label className='flex items-start gap-3 text-sm text-foreground'>
            <input
              type='checkbox'
              name='share-consent'
              checked={shareConsentChecked}
              onChange={(ev) => {
                setShareConsentChecked(ev.target.checked);
                setLocalError((current) =>
                  current ===
                  'You must agree to share your data before sending email.'
                    ? null
                    : current,
                );
              }}
              disabled={busy}
              className='mt-0.5 size-4 rounded border border-border text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-600 disabled:opacity-60'
            />
            <span>By adding your email you agree to share your data</span>
          </label>
          {!matchResultsId ? (
            <p className='text-sm text-amber-800' role='alert'>
              Persisted share ID missing — complete a new match to enable email.
            </p>
          ) : null}
          {localError ? (
            <p className='text-sm text-red-600' role='alert'>
              {localError}
            </p>
          ) : null}
          </DialogBody>
        </form>

        <DialogFooter>
          <Button
            type='button'
            variant='secondary'
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            form={formId}
            variant='primary'
            disabled={busy || !matchResultsId || !shareConsentChecked}
            isLoading={busy}
          >
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
